import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  CurrencyNgn, Wallet, WarningCircle, Truck, FileText, HandCoins, ClipboardText,
} from "@phosphor-icons/react";
import AdminLayout from "@/components/AdminLayout";
import { OpBadge, PayBadge } from "@/components/StatusBadge";
import api from "@/lib/api";
import { naira, fmtDate } from "@/lib/format";

export default function AdminDashboard() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/admin/dashboard").then((r) => setData(r.data)).catch(() => {}); }, []);

  if (!data) return <AdminLayout><div className="text-slate-500 py-20 text-center">Loading…</div></AdminLayout>;
  const { stats, queues } = data;

  const kpis = [
    { label: "Total Sales", value: naira(stats.total_sales), icon: CurrencyNgn, accent: "text-[#0A4F35]" },
    { label: "Payments Received", value: naira(stats.payments_received), icon: HandCoins, accent: "text-[#16A34A]" },
    { label: "Total Outstanding", value: naira(stats.total_outstanding), icon: Wallet, accent: "text-[#DC2626]" },
    { label: "Overdue Invoices", value: stats.overdue_invoices, icon: WarningCircle, accent: "text-[#DC2626]" },
  ];
  const counts = [
    { label: "Awaiting Quote", value: stats.orders_awaiting_quote, icon: FileText },
    { label: "Quotes Awaiting Acceptance", value: stats.quotes_awaiting_acceptance, icon: ClipboardText },
    { label: "Awaiting Payment", value: stats.orders_awaiting_payment, icon: Wallet },
    { label: "Ready for Dispatch", value: stats.ready_for_dispatch, icon: Truck },
    { label: "Deliveries In Progress", value: stats.deliveries_in_progress, icon: Truck },
    { label: "Fully Paid", value: stats.fully_paid, icon: HandCoins },
  ];

  return (
    <AdminLayout>
      <h1 className="font-display text-3xl font-bold text-slate-900">Dashboard</h1>
      <p className="mt-1 text-slate-500">Operational and financial overview.</p>

      <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {kpis.map((k) => (
          <div key={k.label} data-testid={`kpi-${k.label.toLowerCase().replace(/\s+/g, "-")}`} className="rounded-lg border border-[#E5E7EB] bg-white p-5">
            <div className="flex items-center justify-between">
              <span className="text-xs uppercase tracking-widest text-slate-400">{k.label}</span>
              <k.icon size={20} className={k.accent} />
            </div>
            <p className={`font-display text-2xl font-extrabold mt-2 ${k.accent}`}>{k.value}</p>
          </div>
        ))}
      </div>

      <div className="mt-4 grid grid-cols-2 lg:grid-cols-3 gap-4">
        {counts.map((c) => (
          <div key={c.label} className="rounded-lg border border-[#E5E7EB] bg-white p-4 flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-md bg-[#0A4F35]/10 text-[#0A4F35]"><c.icon size={20} /></div>
            <div>
              <p className="font-display text-xl font-bold text-slate-900">{c.value}</p>
              <p className="text-xs text-slate-500">{c.label}</p>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-8 grid lg:grid-cols-2 gap-6">
        <Queue title="Orders Awaiting Quote" rows={queues.awaiting_quote} empty="No orders waiting for a quote." />
        <Queue title="Quotes Awaiting Acceptance" rows={queues.quotes_awaiting_acceptance} empty="No pending quotes." />
        <Queue title="Awaiting Payment" rows={queues.awaiting_payment} empty="Nothing awaiting payment." />
        <Queue title="Overdue Invoices" rows={queues.overdue} empty="No overdue invoices." danger />
      </div>
    </AdminLayout>
  );
}

function Queue({ title, rows, empty, danger }) {
  return (
    <div className="rounded-lg border border-[#E5E7EB] bg-white">
      <div className="px-5 py-3 border-b border-[#E5E7EB] flex items-center justify-between">
        <h3 className="font-display font-semibold text-slate-900">{title}</h3>
        <span className={`text-sm font-bold ${danger ? "text-[#DC2626]" : "text-[#0A4F35]"}`}>{rows.length}</span>
      </div>
      {rows.length === 0 ? (
        <p className="px-5 py-6 text-sm text-slate-400">{empty}</p>
      ) : (
        <div className="divide-y divide-[#E5E7EB]">
          {rows.slice(0, 6).map((o) => (
            <Link key={o.id} to={`/admin/orders/${o.id}`} className="flex items-center justify-between px-5 py-3 hover:bg-[#F9FAFB]">
              <div>
                <p className="font-medium text-slate-900 text-sm">{o.order_number}</p>
                <p className="text-xs text-slate-500">{o.company_name} · {fmtDate(o.created_at)}</p>
              </div>
              <div className="text-right">
                <p className="text-sm font-semibold text-slate-900">{naira(o.financials.total)}</p>
                {o.financials.outstanding > 0 && <p className="text-xs text-[#DC2626]">{naira(o.financials.outstanding)} due</p>}
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
